import { useEffect, useState, type RefObject } from 'react';

/** Always shown this close to the top of the scrolling area. */
export const SHOW_NEAR_TOP = 60;
/** Scrolling up by more than this shows the bar again. */
export const SHOW_AFTER_UP = 6;

interface HideOnScrollOptions {
    /** Off from 760px: the header never hides there. */
    enabled: boolean;
    headerRef: RefObject<HTMLElement | null>;
}

interface HideOnScroll {
    hidden: boolean;
    /** The header's height when it hid, for the layout to give back. */
    height: number;
}

function scrollTopOf(target: EventTarget | null): {
    element: Element;
    top: number;
} | null {
    if (target instanceof Document) {
        const element = target.scrollingElement ?? target.documentElement;
        return { element, top: element.scrollTop };
    }
    if (target instanceof Element)
        return { element: target, top: target.scrollTop };
    return null;
}

/**
 * Hides the narrow header on scroll down and shows it on scroll up (UI-12b).
 *
 * Workspace routes scroll their panes, not the window, so the listener is on
 * `document` in the capture phase: every scroll, the window's or a pane's,
 * reaches it, and each scrolling element keeps its own last position.
 *
 * It never hides while a header panel is open (an `aria-expanded="true"`
 * button in the header) or while focus is in the header, and focus moving
 * into the header shows it. Scrolls inside the header's own panels are
 * ignored. With `enabled` off it is always shown.
 */
export function useHideOnScroll({
    enabled,
    headerRef,
}: HideOnScrollOptions): HideOnScroll {
    const [state, setState] = useState<HideOnScroll>({
        hidden: false,
        height: 0,
    });

    useEffect(() => {
        const header = headerRef.current;
        if (!enabled || !header) return;

        const lastTop = new WeakMap<Element, number>();
        let upTravel = 0;

        const show = () =>
            setState((current) =>
                current.hidden
                    ? { hidden: false, height: current.height }
                    : current
            );
        const blocked = () =>
            header.querySelector('[aria-expanded="true"]') !== null ||
            header.contains(document.activeElement);

        function handleScroll(event: Event) {
            const scrolled = scrollTopOf(event.target);
            if (!scrolled || header?.contains(scrolled.element)) return;
            const { element, top } = scrolled;
            const delta = top - (lastTop.get(element) ?? top);
            lastTop.set(element, top);

            if (top <= SHOW_NEAR_TOP) {
                upTravel = 0;
                show();
            } else if (delta > 0) {
                upTravel = 0;
                if (blocked()) return;
                const height = header?.offsetHeight ?? 0;
                setState((current) =>
                    current.hidden ? current : { hidden: true, height }
                );
            } else if (delta < 0) {
                upTravel -= delta;
                if (upTravel > SHOW_AFTER_UP) show();
            }
        }

        document.addEventListener('scroll', handleScroll, {
            capture: true,
            passive: true,
        });
        header.addEventListener('focusin', show);
        return () => {
            document.removeEventListener('scroll', handleScroll, {
                capture: true,
            });
            header.removeEventListener('focusin', show);
            // Back to shown, so turning it on again never starts hidden.
            show();
        };
    }, [enabled, headerRef]);

    return enabled ? state : { hidden: false, height: state.height };
}
